// src/pages/About.jsx
import { Github, Linkedin, Mail } from 'lucide-react';
import { developer } from '../models/portfolioData';
import aboutImg from '../assets/about-me.png';
import resumePDF from '../assets/Ismail_Jabiulla_Flutter_Developer_Resume.pdf';
import { useThemeViewModel } from '../viewmodels/useThemeViewModel';

const FiverrIcon = ({ size = 18, className = "" }) => (
  <svg
    role="img"
    viewBox="110 120 270 270"
    width={size}
    height={size}
    fill="currentColor"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="315.97" cy="162.19" r="26.87" />
    <path d="M345.87,207.66h-123V199.6c0-15.83,15.83-16.13,23.89-16.13,9.25,0,13.44.9,13.44.9v-43.6a155.21,155.21,0,0,0-19.71-1.19c-25.68,0-73.16,7.16-73.16,61.51V208h-22.4v40.31h22.4v85.1h-20.9v40.31H247.34V333.37H222.85v-85.1H290v85.1H269.13v40.31h97.65V333.37H345.87Z" />
  </svg>
);

export default function About() {
  const { isDark } = useThemeViewModel();

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = resumePDF;
    link.download = 'Ismail_Jabiulla_Flutter_Developer_Resume.pdf';
    link.click();
  };

  const streakUrl = `https://streak-stats.demolab.com/?user=mrjabiulla&theme=${
    isDark ? 'dark' : 'default'
  }&hide_border=true&background=00000000&ring=695aa6&fire=695aa6&currStreakNum=695aa6`;

  return (
    <section className="section pt-0" id="about">
      <div className="container text-center">
        <div className="about">
          {/* Image Holder with transparent background */}
          <div className="about-img-holder">
            <img
              src={aboutImg}
              className="about-img drop-shadow-xl transition-transform duration-500 hover:scale-[1.02]"
              alt={developer.name}
            />
          </div>

          {/* Caption */}
          <div className="about-caption">
            <p className="section-subtitle">Who Am I ?</p>
            <h2 className="section-title mb-3">About Me</h2>
            <p className="mb-6">
              {developer.intro}
            </p>

            {/* Stats Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 my-6 text-center">
              {developer.stats.map(({ num, label }) => (
                <div key={label} className="p-3 bg-gray-50 dark:bg-[#1a1a26] border border-gray-100 dark:border-[#272738] rounded">
                  <div className="font-heading text-2xl text-primary font-bold">{num}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</div>
                </div>
              ))}
            </div>

            {/* Actions Row: Social Links (Left) & Download CV (Right, max space) */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 w-full">
              <div className="flex items-center gap-2">
                <a
                  href={developer.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub"
                  title="GitHub"
                  className="w-10 h-10 rounded-full border border-primary text-primary hover:bg-primary hover:text-white dark:border-primary dark:text-primary dark:hover:bg-primary dark:hover:text-white flex items-center justify-center transition-all duration-300 shadow-sm"
                >
                  <Github size={18} />
                </a>
                <a
                  href={developer.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  title="LinkedIn"
                  className="w-10 h-10 rounded-full border border-primary text-primary hover:bg-primary hover:text-white dark:border-primary dark:text-primary dark:hover:bg-primary dark:hover:text-white flex items-center justify-center transition-all duration-300 shadow-sm"
                >
                  <Linkedin size={18} />
                </a>
                {developer.fiverr && (
                  <a
                    href={developer.fiverr}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Fiverr"
                    title="Fiverr"
                    className="w-10 h-10 rounded-full border border-primary text-primary hover:bg-primary hover:text-white dark:border-primary dark:text-primary dark:hover:bg-primary dark:hover:text-white flex items-center justify-center transition-all duration-300 shadow-sm"
                  >
                    <FiverrIcon size={18} />
                  </a>
                )}
                <a
                  href={`mailto:${developer.email}`}
                  aria-label="Email"
                  title="Email"
                  className="w-10 h-10 rounded-full border border-primary text-primary hover:bg-primary hover:text-white dark:border-primary dark:text-primary dark:hover:bg-primary dark:hover:text-white flex items-center justify-center transition-all duration-300 shadow-sm"
                >
                  <Mail size={18} />
                </a>
              </div>

              <button
                onClick={handleDownload}
                className="btn-rounded btn btn-outline-primary"
              >
                Download CV
              </button>
            </div>
          </div>
        </div>

        {/* GitHub Streak Stats (Transparent & Dynamic Theme) */}
        <div className="mt-20 md:mt-28 pt-4 text-center">
          <h4 className="font-heading text-sm uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 mb-4">
            Github Activities
          </h4>

          <div className="flex justify-center">
            <a
              href={developer.github}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block transition-transform duration-300 hover:scale-[1.02]"
              title="GitHub Streak Stats"
            >
              <img
                src={streakUrl}
                alt="Github Activities"
                className="max-w-full h-auto drop-shadow-sm"
                loading="lazy"
              />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
